import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Loader2, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const propertyTypes = ['Plot', 'House', 'Commercial', 'Any'];
const locations = ['Shivpuri', 'Jamul', 'Bhilai', 'Khedamara', 'Any'];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    property_type: '',
    preferred_location: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source: 'contact'
      });
      toast.success('Message sent! We will contact you soon.');
      setFormData({
        name: '',
        mobile: '',
        email: '',
        property_type: '',
        preferred_location: '',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16 lg:pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-brand-rust to-brand-orange py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
            Contact Us
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Get in touch with us for property enquiries, site visits, or any questions
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
                Office Details
              </h2>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand-orange to-brand-rust rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Office Address</h3>
                    <p className="text-gray-600">
                      Shivpuri, Jamul, Bhilai<br />
                      District – Durg, Chhattisgarh 490024
                    </p>
                    <a 
                      href="https://maps.app.goo.gl/ZZ6YU1F7tCe8F1tx6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-brand-orange hover:underline mt-2 text-sm"
                    >
                      View on Google Maps →
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand-orange to-brand-rust rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">
                      <a href="tel:7581061649" className="hover:text-brand-orange">7581061649</a><br />
                      <a href="tel:9131709809" className="hover:text-brand-orange">9131709809</a>
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand-orange to-brand-rust rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a 
                      href="mailto:contact@shreebajrangdevelopers.com"
                      className="text-gray-600 hover:text-brand-orange"
                    >
                      contact@shreebajrangdevelopers.com
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand-orange to-brand-rust rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Office Hours</h3>
                    <p className="text-gray-600">
                      Monday - Saturday: 9:00 AM - 6:00 PM<br />
                      Sunday: By Appointment
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Contact Buttons */}
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="tel:7581061649">
                  <Button className="bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full">
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </Button>
                </a>
                <a href="https://wa.me/9131709809" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>

              {/* Map Embed */}
              <div className="mt-8 rounded-xl overflow-hidden border h-64">
                <iframe
                  src="https://maps.google.com/maps?q=Shivpuri,+Jamul,+Bhilai,+Chhattisgarh&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                />
              </div>
              <a 
                href="https://maps.app.goo.gl/ZZ6YU1F7tCe8F1tx6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-brand-orange hover:underline mt-3 text-sm"
              >
                <MapPin className="h-4 w-4 mr-1" />
                Open in Google Maps →
              </a>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-2xl border p-6 lg:p-8 shadow-lg">
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your full name"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mobile">Mobile *</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        placeholder="Your mobile number"
                        required
                        data-testid="contact-mobile-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Your email address"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Property Type of Interest</Label>
                      <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                        <SelectTrigger data-testid="contact-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {propertyTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Location Interest</Label>
                      <Select value={formData.preferred_location} onValueChange={(v) => setFormData({ ...formData, preferred_location: v })}>
                        <SelectTrigger data-testid="contact-location-select">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((loc) => (
                            <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Requirements</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your property requirements..."
                      rows={5}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full py-6"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
